import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';

import {
  IcAddCallIcon,
  IcCallLink24Icon,
  IcChannelIcon,
  IcDeletePbIcon,
  IcSpaceIcon,
  IcUserIcon,
  IcUsersIcon,
  IcSecureE2ee24Icon,
} from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IPopover, usePopover, IPopoverMenuItem } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { CreateChannelPopup } from 'APP/main/PopupManager/Popups/CreateChannelPopup/CreateChannelPopup';
import { CreateChatGroupPopup } from 'APP/main/PopupManager/Popups/CreateChatGroupPopup/CreateChatGroupPopup';
import { CreateSecureChatGroupPopup } from 'APP/main/PopupManager/Popups/CreateSecureChatGroupPopup/CreateSecureChatGroupPopup';
import { CreateSpacePopup } from 'APP/main/PopupManager/Popups/CreateSpacePopup/CreateSpacePopup';
import { NewContactPopup } from 'APP/main/PopupManager/Popups/NewContactPopup/NewContactPopup';
import { NewMeetingPopup } from 'APP/main/PopupManager/Popups/NewMeetingPopup/NewMeetingPopup';
import { NewMessagePopup } from 'APP/main/PopupManager/Popups/NewMessagePopup/NewMessagePopup';
import { Features } from 'APP/packages/features/Features';
import { useNavigateTo } from 'APP/router/hooks/useNavigateTo';
import Entities from 'STORE';

interface INewMessageButtonPresenter {
  menuItems: IPopoverMenuItem[];
  myPhone: string;
  newSharedCall(): Promise<void>;
  newMessage(): void;
  newContact(): void;
  newGroupChat(): void;
  newSecureChat(): void;
  newSpace(): void;
  newChannel(): void;
  onNewMeetingClick(): void;
}

interface IUseNewMessageButtonPresenter {
  presenter: INewMessageButtonPresenter;
  popover: IPopover<HTMLButtonElement>;
}

export function useNewMessageButtonPresenter(): IUseNewMessageButtonPresenter {
  const popover = usePopover<HTMLButtonElement>();
  const { t } = useTranslation();
  const navigateTo = useNavigateTo();
  const source = useAsObservableSource({ navigateTo });

  const presenter = useLocalStore<INewMessageButtonPresenter>(() => ({
    get menuItems(): IPopoverMenuItem[] {
      return [
        {
          key: 'new-message',
          title: t('new_message'),
          onClick: presenter.newMessage,
          icon: IcUserIcon,
          isVisible: true,
        },
        {
          key: 'new-secure-chat',
          title: t('secure_chat_create_button'),
          onClick: presenter.newSecureChat,
          icon: IcSecureE2ee24Icon,
          isVisible: Entities.featuresStore.isE2eeIntegrationEnabled,
        },
        {
          key: 'new_meeting',
          title: t('main_menu_new_meeting_button'),
          onClick: presenter.onNewMeetingClick,
          icon: IcCallLink24Icon,
          isVisible: true,
        },
        {
          key: 'new-shared-call',
          title: t('call_start_new'),
          onClick: presenter.newSharedCall,
          icon: IcAddCallIcon,
          isVisible: Features.isFlexibleCallEnabled(),
        },
        {
          key: 'new-contact',
          title: t('new_contact'),
          onClick: presenter.newContact,
          icon: IcDeletePbIcon,
          isVisible: Features.isContactsEnabled(),
        },
        {
          key: 'new-group-chat',
          title: t('new_group_chat'),
          onClick: presenter.newGroupChat,
          icon: IcUsersIcon,
          isVisible: true,
        },
        {
          key: 'new-space',
          title: t('space_new_space'),
          onClick: presenter.newSpace,
          icon: IcSpaceIcon,
          isVisible: Features.isSpacesEnabled(),
        },
        {
          key: 'new-channel',
          title: t('new_channel'),
          onClick: presenter.newChannel,
          icon: IcChannelIcon,
          isVisible: true,
        },
      ].filter((item) => item.isVisible);
    },

    get myPhone(): string {
      return Entities.usersStore.Me.phone || '';
    },

    async newSharedCall(): Promise<void> {
      const group = await Tasks.calls.startSharedCallAndCreateGroup();

      if (group) {
        source.navigateTo({
          to: `/${group.route}/${group.id}`,
        });
      }
    },

    newMessage(): void {
      Tasks.app.openPopup(NewMessagePopup);
    },

    onNewMeetingClick(): void {
      Tasks.app.openPopup(NewMeetingPopup);
    },

    newContact(): void {
      Tasks.app.openPopup(NewContactPopup, {
        params: { navigateToActiveGroup: true },
      });
    },

    newGroupChat(): void {
      Tasks.app.openPopup(CreateChatGroupPopup, {});
    },

    newSecureChat(): void {
      Tasks.app.openPopup(CreateSecureChatGroupPopup, {
        params: {},
      });
    },

    newSpace(): void {
      Tasks.app.openPopup(CreateSpacePopup);
    },

    newChannel(): void {
      Tasks.app.openPopup(CreateChannelPopup);
    },
  }));

  return { presenter, popover };
}
